import { Check, FileCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { ChecklistItem } from "./derive";

/** Right column of the home hero: "X of N done" progress + the checklist items.
 * Falls back to a reassuring state when the firm has nothing outstanding. */
export function ChecklistCard({ items }: { items: ChecklistItem[] }) {
  const total = items.length;
  const done = items.filter((i) => i.done).length;
  const pct = total === 0 ? 100 : Math.round((done / total) * 100);

  return (
    <Card className="relative gap-4 overflow-hidden p-7">
      <div className="bg-primary-soft text-warning absolute top-5 right-5 grid size-12 place-items-center rounded-sm">
        <FileCheck className="size-6" />
      </div>
      <div>
        <div className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          Your checklist
        </div>
        <div className="font-heading mt-1 text-3xl leading-tight font-bold text-foreground">
          {total === 0 ? "All done" : `${done} of ${total} done`}
        </div>
      </div>
      <Progress value={pct} className="h-3" />
      {total === 0 ? (
        <p className="text-muted-foreground text-sm">
          Nothing outstanding right now — we'll let you know the moment we need something.
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5 text-sm">
          {items.map((item, i) => (
            <li
              key={`${item.label}-${i}`}
              className={
                item.done
                  ? "text-muted-foreground flex items-center gap-2.5"
                  : "text-foreground flex items-center gap-2.5 font-semibold"
              }
            >
              {item.done ? (
                <span className="bg-success-soft text-success grid size-[22px] shrink-0 place-items-center rounded-full">
                  <Check className="size-3" />
                </span>
              ) : (
                <span className="text-warning grid size-[22px] shrink-0 place-items-center rounded-full border-2 border-primary text-[11px] font-bold">
                  {i + 1}
                </span>
              )}
              <span className={item.done ? "line-through" : ""}>{item.label}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
